/**
 * Helper para llamar al BFF Aosenuma (apps/api-edge) desde las SPAs.
 *
 * - Auto-inyecta `Authorization: Bearer <access_token>` desde la sesión Supabase.
 * - Resuelve la URL base desde `VITE_PUBLIC_API_BASE_URL` (default localhost:8787 en dev).
 * - Lanza con detalle si la respuesta no es 2xx.
 *
 * Uso:
 *   const { data } = await bffFetch<{ ok: true }>('/v1/captura', {
 *     method: 'POST',
 *     body: payload,
 *   });
 */
import { getSupabase } from '../client';

function resolveApiBase(): string {
  // Accedemos via Record para no requerir ImportMetaEnv augment en cada app.
  const env = (
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env as unknown as Record<string, string | undefined>)
      : {}
  );
  const raw = env.VITE_PUBLIC_API_BASE_URL?.trim();
  if (raw && raw.length > 0) return raw.replace(/\/$/, '');
  return 'http://localhost:8787';
}

const API_BASE: string = resolveApiBase();

export interface BffFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export class BffError extends Error {
  status: number;
  detail?: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'BffError';
    this.status = status;
    this.detail = detail;
  }
}

export async function bffFetch<T = unknown>(
  path: string,
  opts: BffFetchOptions = {},
): Promise<T> {
  const sb = getSupabase();
  const { data: sessionData } = await sb.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new BffError(401, 'No hay sesión activa. Inicia sesión.');
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    ...opts.headers,
  };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] ??= 'application/json';
    body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
  }

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body,
    signal: opts.signal,
  });

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    const msg =
      typeof detail === 'object' && detail !== null && 'error' in detail
        ? String((detail as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new BffError(res.status, msg, detail);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.arrayBuffer()) as unknown as T;
}

/**
 * Variante de bffFetch que NO requiere sesión: para endpoints anon-callable
 * del BFF (ej: /v1/auth/check-email, llamado pre-login). Misma resolución
 * de API_BASE + manejo de error, sin Authorization header.
 *
 * Usar con criterio: solo endpoints diseñados para ser anon. La mayoría
 * del BFF requiere JWT — usá `bffFetch` para esos.
 */
export async function bffFetchPublic<T = unknown>(
  path: string,
  opts: BffFetchOptions = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] ??= 'application/json';
    body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
  }

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body,
    signal: opts.signal,
  });

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    const msg =
      typeof detail === 'object' && detail !== null && 'error' in detail
        ? String((detail as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new BffError(res.status, msg, detail);
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.arrayBuffer()) as unknown as T;
}
